/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */
import * as coreClient from "@azure/core-client";
import * as coreHttpCompat from "@azure/core-http-compat";
import * as Parameters from "./models/parameters";
import * as Mappers from "./models/mappers";
/** @internal */
export class AppConfiguration extends coreHttpCompat.ExtendedServiceClient {
    /**
     * Initializes a new instance of the AppConfiguration class.
     * @param endpoint The endpoint of the App Configuration instance to send requests to.
     * @param apiVersion Api Version
     * @param options The parameter options
     */
    constructor(endpoint, apiVersion, options) {
        if (endpoint === undefined) {
            throw new Error("'endpoint' cannot be null");
        }
        if (apiVersion === undefined) {
            throw new Error("'apiVersion' cannot be null");
        }
        // Initializing default values for options
        if (!options) {
            options = {};
        }
        const defaults = {
            requestContentType: "application/json; charset=utf-8"
        };
        const packageDetails = `azsdk-js-app-configuration/1.4.1`;
        const userAgentPrefix = options.userAgentOptions && options.userAgentOptions.userAgentPrefix
            ? `${options.userAgentOptions.userAgentPrefix} ${packageDetails}`
            : `${packageDetails}`;
        const optionsWithDefaults = Object.assign(Object.assign(Object.assign({}, defaults), options), { userAgentOptions: {
                userAgentPrefix
            }, baseUri: options.endpoint || "{endpoint}" });
        super(optionsWithDefaults);
        // Parameter assignments
        this.endpoint = endpoint;
        this.apiVersion = apiVersion;
    }
    /**
     * Gets a list of keys.
     * @param options The options parameters.
     */
    getKeys(options) {
        return this.sendOperationRequest({ options }, getKeysOperationSpec);
    }
    /**
     * Requests the headers and status of the given resource.
     * @param options The options parameters.
     */
    checkKeys(options) {
        return this.sendOperationRequest({ options }, checkKeysOperationSpec);
    }
    /**
     * Gets a list of key-values.
     * @param options The options parameters.
     */
    getKeyValues(options) {
        return this.sendOperationRequest({ options }, getKeyValuesOperationSpec);
    }
    /**
     * Requests the headers and status of the given resource.
     * @param options The options parameters.
     */
    checkKeyValues(options) {
        return this.sendOperationRequest({ options }, checkKeyValuesOperationSpec);
    }
    /**
     * Gets a single key-value.
     * @param key The key of the key-value to retrieve.
     * @param options The options parameters.
     */
    getKeyValue(key, options) {
        return this.sendOperationRequest({ key, options }, getKeyValueOperationSpec);
    }
    /**
     * Creates a key-value.
     * @param key The key of the key-value to create.
     * @param options The options parameters.
     */
    putKeyValue(key, options) {
        return this.sendOperationRequest({ key, options }, putKeyValueOperationSpec);
    }
    /**
     * Deletes a key-value.
     * @param key The key of the key-value to delete.
     * @param options The options parameters.
     */
    deleteKeyValue(key, options) {
        return this.sendOperationRequest({ key, options }, deleteKeyValueOperationSpec);
    }
    /**
     * Requests the headers and status of the given resource.
     * @param key The key of the key-value to retrieve.
     * @param options The options parameters.
     */
    checkKeyValue(key, options) {
        return this.sendOperationRequest({ key, options }, checkKeyValueOperationSpec);
    }
    /**
     * Gets a list of labels.
     * @param options The options parameters.
     */
    getLabels(options) {
        return this.sendOperationRequest({ options }, getLabelsOperationSpec);
    }
    /**
     * Requests the headers and status of the given resource.
     * @param options The options parameters.
     */
    checkLabels(options) {
        return this.sendOperationRequest({ options }, checkLabelsOperationSpec);
    }
    /**
     * Locks a key-value.
     * @param key The key of the key-value to lock.
     * @param options The options parameters.
     */
    putLock(key, options) {
        return this.sendOperationRequest({ key, options }, putLockOperationSpec);
    }
    /**
     * Unlocks a key-value.
     * @param key The key of the key-value to unlock.
     * @param options The options parameters.
     */
    deleteLock(key, options) {
        return this.sendOperationRequest({ key, options }, deleteLockOperationSpec);
    }
    /**
     * Gets a list of key-value revisions.
     * @param options The options parameters.
     */
    getRevisions(options) {
        return this.sendOperationRequest({ options }, getRevisionsOperationSpec);
    }
    /**
     * Requests the headers and status of the given resource.
     * @param options The options parameters.
     */
    checkRevisions(options) {
        return this.sendOperationRequest({ options }, checkRevisionsOperationSpec);
    }
    /**
     * GetKeysNext
     * @param nextLink The nextLink from the previous successful call to the GetKeys method.
     * @param options The options parameters.
     */
    getKeysNext(nextLink, options) {
        return this.sendOperationRequest({ nextLink, options }, getKeysNextOperationSpec);
    }
    /**
     * GetKeyValuesNext
     * @param nextLink The nextLink from the previous successful call to the GetKeyValues method.
     * @param options The options parameters.
     */
    getKeyValuesNext(nextLink, options) {
        return this.sendOperationRequest({ nextLink, options }, getKeyValuesNextOperationSpec);
    }
    /**
     * GetLabelsNext
     * @param nextLink The nextLink from the previous successful call to the GetLabels method.
     * @param options The options parameters.
     */
    getLabelsNext(nextLink, options) {
        return this.sendOperationRequest({ nextLink, options }, getLabelsNextOperationSpec);
    }
    /**
     * GetRevisionsNext
     * @param nextLink The nextLink from the previous successful call to the GetRevisions method.
     * @param options The options parameters.
     */
    getRevisionsNext(nextLink, options) {
        return this.sendOperationRequest({ nextLink, options }, getRevisionsNextOperationSpec);
    }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);
const getKeysOperationSpec = {
    path: "/keys",
    httpMethod: "GET",
    responses: {
        200: {
            bodyMapper: Mappers.KeyListResult,
            headersMapper: Mappers.AppConfigurationGetKeysHeaders
        },
        default: {
            bodyMapper: Mappers.ErrorModel
        }
    },
    queryParameters: [Parameters.name, Parameters.apiVersion, Parameters.after],
    urlParameters: [Parameters.endpoint],
    headerParameters: [
        Parameters.accept,
        Parameters.syncToken,
        Parameters.acceptDatetime
    ],
    serializer
};
const checkKeysOperationSpec = {
    path: "/keys",
    httpMethod: "HEAD",
    responses: {
        200: {
            headersMapper: Mappers.AppConfigurationCheckKeysHeaders
        },
        default: {}
    },
    queryParameters: [Parameters.name, Parameters.apiVersion, Parameters.after],
    urlParameters: [Parameters.endpoint],
    headerParameters: [Parameters.syncToken, Parameters.acceptDatetime],
    serializer
};
const getKeyValuesOperationSpec = {
    path: "/kv",
    httpMethod: "GET",
    responses: {
        200: {
            bodyMapper: Mappers.KeyValueListResult,
            headersMapper: Mappers.AppConfigurationGetKeyValuesHeaders
        },
        default: {
            bodyMapper: Mappers.ErrorModel
        }
    },
    queryParameters: [
        Parameters.apiVersion,
        Parameters.after,
        Parameters.key,
        Parameters.label,
        Parameters.select
    ],
    urlParameters: [Parameters.endpoint],
    headerParameters: [
        Parameters.syncToken,
        Parameters.acceptDatetime,
        Parameters.accept1
    ],
    serializer
};
const checkKeyValuesOperationSpec = {
    path: "/kv",
    httpMethod: "HEAD",
    responses: {
        200: {
            headersMapper: Mappers.AppConfigurationCheckKeyValuesHeaders
        },
        default: {}
    },
    queryParameters: [
        Parameters.apiVersion,
        Parameters.after,
        Parameters.key,
        Parameters.label,
        Parameters.select
    ],
    urlParameters: [Parameters.endpoint],
    headerParameters: [Parameters.syncToken, Parameters.acceptDatetime],
    serializer
};
const getKeyValueOperationSpec = {
    path: "/kv/{key}",
    httpMethod: "GET",
    responses: {
        200: {
            bodyMapper: Mappers.KeyValue,
            headersMapper: Mappers.AppConfigurationGetKeyValueHeaders
        },
        304: {
            headersMapper: Mappers.AppConfigurationGetKeyValueHeaders
        },
        default: {
            bodyMapper: Mappers.ErrorModel
        }
    },
    queryParameters: [Parameters.apiVersion, Parameters.label, Parameters.select],
    urlParameters: [Parameters.endpoint, Parameters.key1],
    headerParameters: [
        Parameters.syncToken,
        Parameters.acceptDatetime,
        Parameters.accept2,
        Parameters.ifMatch,
        Parameters.ifNoneMatch
    ],
    serializer
};
const putKeyValueOperationSpec = {
    path: "/kv/{key}",
    httpMethod: "PUT",
    responses: {
        200: {
            bodyMapper: Mappers.KeyValue,
            headersMapper: Mappers.AppConfigurationPutKeyValueHeaders
        },
        default: {
            bodyMapper: Mappers.ErrorModel
        }
    },
    requestBody: Parameters.entity,
    queryParameters: [Parameters.apiVersion, Parameters.label],
    urlParameters: [Parameters.endpoint, Parameters.key1],
    headerParameters: [
        Parameters.syncToken,
        Parameters.accept2,
        Parameters.ifMatch,
        Parameters.ifNoneMatch,
        Parameters.contentType
    ],
    mediaType: "json",
    serializer
};
const deleteKeyValueOperationSpec = {
    path: "/kv/{key}",
    httpMethod: "DELETE",
    responses: {
        200: {
            bodyMapper: Mappers.KeyValue,
            headersMapper: Mappers.AppConfigurationDeleteKeyValueHeaders
        },
        204: {
            headersMapper: Mappers.AppConfigurationDeleteKeyValueHeaders
        },
        default: {
            bodyMapper: Mappers.ErrorModel
        }
    },
    queryParameters: [Parameters.apiVersion, Parameters.label],
    urlParameters: [Parameters.endpoint, Parameters.key1],
    headerParameters: [
        Parameters.syncToken,
        Parameters.accept2,
        Parameters.ifMatch
    ],
    serializer
};
const checkKeyValueOperationSpec = {
    path: "/kv/{key}",
    httpMethod: "HEAD",
    responses: {
        200: {
            headersMapper: Mappers.AppConfigurationCheckKeyValueHeaders
        },
        304: {
            headersMapper: Mappers.AppConfigurationCheckKeyValueHeaders
        },
        default: {}
    },
    queryParameters: [Parameters.apiVersion, Parameters.label, Parameters.select],
    urlParameters: [Parameters.endpoint, Parameters.key1],
    headerParameters: [
        Parameters.syncToken,
        Parameters.acceptDatetime,
        Parameters.ifMatch,
        Parameters.ifNoneMatch
    ],
    serializer
};
const getLabelsOperationSpec = {
    path: "/labels",
    httpMethod: "GET",
    responses: {
        200: {
            bodyMapper: Mappers.LabelListResult,
            headersMapper: Mappers.AppConfigurationGetLabelsHeaders
        },
        default: {
            bodyMapper: Mappers.ErrorModel
        }
    },
    queryParameters: [
        Parameters.name,
        Parameters.apiVersion,
        Parameters.after,
        Parameters.select1
    ],
    urlParameters: [Parameters.endpoint],
    headerParameters: [
        Parameters.syncToken,
        Parameters.acceptDatetime,
        Parameters.accept3
    ],
    serializer
};
const checkLabelsOperationSpec = {
    path: "/labels",
    httpMethod: "HEAD",
    responses: {
        200: {
            headersMapper: Mappers.AppConfigurationCheckLabelsHeaders
        },
        default: {}
    },
    queryParameters: [
        Parameters.name,
        Parameters.apiVersion,
        Parameters.after,
        Parameters.select1
    ],
    urlParameters: [Parameters.endpoint],
    headerParameters: [Parameters.syncToken, Parameters.acceptDatetime],
    serializer
};
const putLockOperationSpec = {
    path: "/locks/{key}",
    httpMethod: "PUT",
    responses: {
        200: {
            bodyMapper: Mappers.KeyValue,
            headersMapper: Mappers.AppConfigurationPutLockHeaders
        },
        default: {
            bodyMapper: Mappers.ErrorModel
        }
    },
    queryParameters: [Parameters.apiVersion, Parameters.label],
    urlParameters: [Parameters.endpoint, Parameters.key1],
    headerParameters: [
        Parameters.syncToken,
        Parameters.accept2,
        Parameters.ifMatch,
        Parameters.ifNoneMatch
    ],
    serializer
};
const deleteLockOperationSpec = {
    path: "/locks/{key}",
    httpMethod: "DELETE",
    responses: {
        200: {
            bodyMapper: Mappers.KeyValue,
            headersMapper: Mappers.AppConfigurationDeleteLockHeaders
        },
        default: {
            bodyMapper: Mappers.ErrorModel
        }
    },
    queryParameters: [Parameters.apiVersion, Parameters.label],
    urlParameters: [Parameters.endpoint, Parameters.key1],
    headerParameters: [
        Parameters.syncToken,
        Parameters.accept2,
        Parameters.ifMatch,
        Parameters.ifNoneMatch
    ],
    serializer
};
const getRevisionsOperationSpec = {
    path: "/revisions",
    httpMethod: "GET",
    responses: {
        200: {
            bodyMapper: Mappers.KeyValueListResult,
            headersMapper: Mappers.AppConfigurationGetRevisionsHeaders
        },
        default: {
            bodyMapper: Mappers.ErrorModel
        }
    },
    queryParameters: [
        Parameters.apiVersion,
        Parameters.after,
        Parameters.key,
        Parameters.label,
        Parameters.select
    ],
    urlParameters: [Parameters.endpoint],
    headerParameters: [
        Parameters.syncToken,
        Parameters.acceptDatetime,
        Parameters.accept1
    ],
    serializer
};
const checkRevisionsOperationSpec = {
    path: "/revisions",
    httpMethod: "HEAD",
    responses: {
        200: {
            headersMapper: Mappers.AppConfigurationCheckRevisionsHeaders
        },
        default: {}
    },
    queryParameters: [
        Parameters.apiVersion,
        Parameters.after,
        Parameters.key,
        Parameters.label,
        Parameters.select
    ],
    urlParameters: [Parameters.endpoint],
    headerParameters: [Parameters.syncToken, Parameters.acceptDatetime],
    serializer
};
const getKeysNextOperationSpec = {
    path: "{nextLink}",
    httpMethod: "GET",
    responses: {
        200: {
            bodyMapper: Mappers.KeyListResult,
            headersMapper: Mappers.AppConfigurationGetKeysNextHeaders
        },
        default: {
            bodyMapper: Mappers.ErrorModel
        }
    },
    queryParameters: [Parameters.name, Parameters.apiVersion, Parameters.after],
    urlParameters: [Parameters.endpoint, Parameters.nextLink],
    headerParameters: [
        Parameters.accept,
        Parameters.syncToken,
        Parameters.acceptDatetime
    ],
    serializer
};
const getKeyValuesNextOperationSpec = {
    path: "{nextLink}",
    httpMethod: "GET",
    responses: {
        200: {
            bodyMapper: Mappers.KeyValueListResult,
            headersMapper: Mappers.AppConfigurationGetKeyValuesNextHeaders
        },
        default: {
            bodyMapper: Mappers.ErrorModel
        }
    },
    queryParameters: [
        Parameters.apiVersion,
        Parameters.after,
        Parameters.key,
        Parameters.label,
        Parameters.select
    ],
    urlParameters: [Parameters.endpoint, Parameters.nextLink],
    headerParameters: [
        Parameters.syncToken,
        Parameters.acceptDatetime,
        Parameters.accept1
    ],
    serializer
};
const getLabelsNextOperationSpec = {
    path: "{nextLink}",
    httpMethod: "GET",
    responses: {
        200: {
            bodyMapper: Mappers.LabelListResult,
            headersMapper: Mappers.AppConfigurationGetLabelsNextHeaders
        },
        default: {
            bodyMapper: Mappers.ErrorModel
        }
    },
    queryParameters: [
        Parameters.name,
        Parameters.apiVersion,
        Parameters.after,
        Parameters.select1
    ],
    urlParameters: [Parameters.endpoint, Parameters.nextLink],
    headerParameters: [
        Parameters.syncToken,
        Parameters.acceptDatetime,
        Parameters.accept3
    ],
    serializer
};
const getRevisionsNextOperationSpec = {
    path: "{nextLink}",
    httpMethod: "GET",
    responses: {
        200: {
            bodyMapper: Mappers.KeyValueListResult,
            headersMapper: Mappers.AppConfigurationGetRevisionsNextHeaders
        },
        default: {
            bodyMapper: Mappers.ErrorModel
        }
    },
    queryParameters: [
        Parameters.apiVersion,
        Parameters.after,
        Parameters.key,
        Parameters.label,
        Parameters.select
    ],
    urlParameters: [Parameters.endpoint, Parameters.nextLink],
    headerParameters: [
        Parameters.syncToken,
        Parameters.acceptDatetime,
        Parameters.accept1
    ],
    serializer
};
//# sourceMappingURL=appConfiguration.js.map